import { buildApiUrl, getApiBaseUrl } from './apiConfig';

const API_TIMEOUT = 30000;

const STORAGE_TOKEN_KEY = 'tiempos_auth_token';

type FetchAPIOptions = RequestInit & {
  timeoutMs?: number;
};

/**
 * Error de API que CONSERVA el cuerpo de la respuesta.
 *
 * `fetchAPI` lanzaba un `Error` con solo el mensaje, así que un 409 como
 *
 *     { "detail": "Ya existe una operación activa",
 *       "operacion": { "operacion_id": "...", "estado": "APLICANDO", ... } }
 *
 * llegaba al llamador convertido en texto: el `operacion_id` se perdía y la
 * pantalla no podía reengancharse a la operación en curso.
 *
 * Extiende `Error`, así que `error.message` sigue funcionando igual para todo
 * el código existente — nadie tiene que cambiar nada. Lo único que se añade es
 * `status` y `payload` para quien los necesite.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(mensaje: string, status: number, payload: unknown) {
    super(mensaje);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
    // Necesario al compilar a ES5: sin esto, `instanceof ApiError` falla.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

function getToken(): string | null {
  return localStorage.getItem(STORAGE_TOKEN_KEY);
}

export async function fetchAPI<T>(
  endpoint: string,
  options: FetchAPIOptions = {}
): Promise<T> {
  const controller = new AbortController();
  const { timeoutMs = API_TIMEOUT, ...requestOptions } = options;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const token = getToken();

  try {
    const response = await fetch(buildApiUrl(endpoint), {
      ...requestOptions,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...requestOptions.headers,
      },
    });

    clearTimeout(timeoutId);

    if (response.status === 401) {
      const body = await response.json().catch(() => ({}));
      const detalle: string = body.detail || '';
      localStorage.removeItem(STORAGE_TOKEN_KEY);
      localStorage.removeItem('tiempos_auth_user');
      // Guardar aviso para mostrar en LoginPage
      const aviso = detalle.toLowerCase().includes('invalidada')
        ? 'Su sesión fue cerrada porque se inició sesión desde otro dispositivo.'
        : 'Su sesión expiró. Inicie sesión nuevamente.';
      sessionStorage.setItem('tiempos_login_aviso', aviso);
      window.location.href = '/login';
      throw new Error(detalle || 'Sesion expirada');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // FIX: el `detail` de un 422 de FastAPI es un ARRAY de objetos; pasarlo
      // directo a Error produce "[object Object]" y el usuario no ve la causa.
      const detail = errorData.detail ?? errorData.error;
      let mensaje: string;
      if (Array.isArray(detail)) {
        mensaje = detail
          .map((d: any) => {
            const campo = Array.isArray(d?.loc) ? d.loc.slice(1).join('.') : '';
            return campo ? `${campo}: ${d?.msg ?? ''}` : (d?.msg ?? JSON.stringify(d));
          })
          .join(' · ');
      } else if (detail && typeof detail === 'object') {
        mensaje = JSON.stringify(detail);
      } else {
        mensaje = detail || `HTTP ${response.status}`;
      }
      // `ApiError` en vez de `Error`: mismo `.message` de siempre, pero además
      // conserva el cuerpo para quien lo necesite (ver clase arriba).
      throw new ApiError(mensaje, response.status, errorData);
    }

    return response.json();
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Conexion lenta. Intente nuevamente.');
    }
    if (error instanceof TypeError) {
      throw new Error('No se pudo conectar con el API. Verifique que el backend este activo.');
    }
    throw error;
  }
}

export { STORAGE_TOKEN_KEY, getApiBaseUrl, buildApiUrl };
